import React, { useState, useEffect, useRef } from 'react';
import Badge from './common/Badge';

const FadeIn = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setTimeout(() => setVisible(true), delay); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div ref={ref} style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transition: 'all 0.6s cubic-bezier(0.22, 1, 0.36, 1)' }}>
      {children}
    </div>
  );
};

const FeatureCard = ({ icon, title, desc }) => (
  <FadeIn>
    <div style={styles.featureCard}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.06)'; e.currentTarget.style.borderColor = '#4cbb4c'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#e8e8e8'; }}
    >
      <div style={styles.featureIcon}>{icon}</div>
      <h3 style={styles.featureTitle}>{title}</h3>
      <p style={styles.featureDesc}>{desc}</p>
    </div>
  </FadeIn>
);

const Step = ({ num, title, desc }) => (
  <FadeIn delay={num * 150}>
    <div style={styles.stepRow}>
      <div style={styles.stepNumWrap}>
        <div style={styles.stepNum}>{num}</div>
        {num < 3 && <div style={styles.stepLine} />}
      </div>
      <div style={styles.stepContent}>
        <h3 style={styles.stepTitle}>{title}</h3>
        <p style={styles.stepDesc}>{desc}</p>
      </div>
    </div>
  </FadeIn>
);

const LandingPage = () => {
  const [examCode, setExamCode] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const joinExam = () => {
    const code = examCode.trim();
    if (!code) return;
    window.location.href = `/exam/${code}`;
  };

  const navLinks = [
    { label: 'Features', href: '#features' },
    { label: 'How It Works', href: '#how-it-works' },
  ];

  return (
    <div style={styles.page}>

      {/* NAV */}
      <nav style={{
        ...styles.nav,
        background: scrolled ? 'rgba(255,255,255,0.92)' : 'transparent',
        backdropFilter: scrolled ? 'blur(14px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(0,0,0,0.06)' : 'none',
        boxShadow: scrolled ? '0 1px 8px rgba(0,0,0,0.04)' : 'none',
      }}>
        <div style={styles.navInner}>
          <div style={styles.logoWrap}>
            <div style={styles.logoMark}>KBA</div>
            <span style={styles.logoText}>ExamMonitor</span>
          </div>

          <div style={styles.navLinks}>
            {navLinks.map(item => (
              <a key={item.href} href={item.href} style={styles.navLink}
                onMouseEnter={e => e.target.style.color = '#1a1a1a'}
                onMouseLeave={e => e.target.style.color = '#666'}>{item.label}</a>
            ))}
          </div>

          <button onClick={() => setMobileOpen(!mobileOpen)} style={styles.hamburger}>☰</button>
        </div>

        {mobileOpen && (
          <div style={styles.mobileMenu}>
            {navLinks.map(item => (
              <a key={item.href} href={item.href} onClick={() => setMobileOpen(false)} style={styles.mobileLink}>{item.label}</a>
            ))}
          </div>
        )}

        <style>{`
          @media(max-width:768px){ .nav-links-desktop{display:none!important} .hamburger-show{display:flex!important} }
          @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
          @keyframes pulse-dot { 0%,100%{opacity:0.3} 50%{opacity:1} }
        `}</style>
      </nav>

      {/* HERO */}
      <section style={styles.hero}>
        <div style={styles.heroGlow1} />
        <div style={styles.heroGlow2} />
        <div style={styles.heroGrid} />

        <div style={styles.heroContent}>
          <Badge style={{ marginBottom: 20 }}>AI-Powered Proctoring!!!</Badge>
          <h1 style={styles.heroTitle}>
            Secure, Intelligent<br />
            <span style={styles.gradientText}>Exam Monitoring</span>
          </h1>
          <p style={styles.heroDesc}>
            Real-time AI proctoring that ensures academic integrity without compromising the student experience. Enter your exam code to begin.
          </p>

          <div style={styles.codeInputWrapper}>
            <input type="text" placeholder="Enter your exam code..." value={examCode}
              onChange={e => setExamCode(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && joinExam()}
              style={styles.codeInput} />
            <button onClick={joinExam} style={styles.joinBtn}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(76,187,76,0.35)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
            >Join Exam →</button>
          </div>
          <p style={styles.heroHint}>
            Ensure your camera and microphone are enabled before joining.
          </p>
        </div>

        <div style={styles.heroVisual}>
          <div style={styles.mockWindow}>
            <div style={styles.mockHeader}>
              <span style={{ ...styles.mockDot, background: '#ff5f56' }} />
              <span style={{ ...styles.mockDot, background: '#ffbd2e' }} />
              <span style={{ ...styles.mockDot, background: '#27c93f' }} />
              <span style={{ marginLeft: 16, fontSize: 11, color: '#888' }}>Proctoring Active</span>
            </div>
            <div style={styles.mockBody}>
              <div style={styles.mockBadge}>👤 FACE DETECTED</div>
              <div style={styles.mockScanline} />
            </div>
            <div style={styles.mockFooter}>
              <span>⏱ 00:00</span>
              <span>⚠ 0 Violations</span>
              <span style={{ color: '#4cbb4c' }}>● REC</span>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section style={styles.trustBar}>
        <div style={styles.trustGrid}>
          {[
            { value: '10K+', label: 'Exams Proctored' },
            { value: '99.7%', label: 'Detection Accuracy' },
            { value: '50K+', label: 'Students Trust Us' },
            { value: '<2s', label: 'Alert Response Time' },
          ].map(s => (
            <FadeIn key={s.value}>
              <div style={{ textAlign: 'center' }}>
                <div style={styles.statValue}>{s.value}</div>
                <div style={styles.statLabel}>{s.label}</div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" style={styles.featuresSection}>
        <div style={styles.sectionHeader}>
          <Badge>Features</Badge>
          <h2 style={styles.sectionTitle}>Everything you need for tamper-proof assessments</h2>
          <p style={styles.sectionSub}>Our AI-driven system continuously monitors multiple signals to detect and deter academic dishonesty in real time.</p>
        </div>
        <div style={styles.featuresGrid}>
          <FeatureCard icon="👤" title="Face Recognition & Tracking" desc="Continuous 68-point facial landmark detection verifies the candidate's identity throughout the entire session." />
          <FeatureCard icon="👁️" title="Gaze & Attention Analysis" desc="Detects off-screen glances, extended gaze away, and absence from the camera frame with high precision." />
          <FeatureCard icon="🔊" title="Audio Anomaly Detection" desc="Flags unauthorized speech, whispered answers, or background conversations that may indicate collusion attempts." />
          <FeatureCard icon="🖥️" title="Tab & Application Tracking" desc="Instantly detects tab switches, window changes, or attempts to navigate away from the exam page." />
          <FeatureCard icon="🎥" title="Full Session Recording" desc="End-to-end video recording with segmented upload ensures every moment is captured for audit and review." />
          <FeatureCard icon="🛡️" title="Integrity Controls" desc="Blocks copy-paste, right-click, DevTools, and suspicious keyboard shortcuts to prevent cheating." />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" style={styles.howSection}>
        <div style={styles.sectionHeader}>
          <Badge variant="dark">How It Works</Badge>
          <h2 style={{ ...styles.sectionTitle, color: '#fff' }}>Three steps to a secure exam experience</h2>
          <p style={{ ...styles.sectionSub, color: '#999' }}>From entry to submission, every phase is monitored and protected by our AI proctor.</p>
        </div>
        <div style={styles.stepsContainer}>
          <Step num={1} title="Enter Exam Code" desc="Paste the unique code provided by your institution into the field above. No sign-up or account required." />
          <Step num={2} title="Identity Verification" desc="Grant camera and microphone access. Our AI performs a quick facial verification to confirm your identity." />
          <Step num={3} title="Begin the Exam" desc="Answer with confidence. Our proctor works silently in the background, only alerting on genuine violations." />
        </div>
      </section>

      {/* CTA */}
      <section style={styles.ctaSection}>
        <FadeIn>
          <Badge>Get Started</Badge>
          <h2 style={{ ...styles.sectionTitle, color: '#fff', marginTop: 16 }}>Ready to take your exam?</h2>
          <p style={{ ...styles.sectionSub, color: '#aaa', maxWidth: 520, margin: '0 auto 32px' }}>
            Enter your exam code above or contact your institution for access credentials.
          </p>
          <button onClick={() => document.querySelector('.code-input')?.focus() || window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={styles.ctaBtn}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(76,187,76,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
          >Join an Exam</button>
        </FadeIn>
      </section>

      {/* FOOTER */}
      <footer style={styles.footer}>
        <div style={styles.footerInner}>
          <div style={{ maxWidth: 280 }}>
            <div style={styles.footerBrand}>
              <div style={styles.logoMarkSmall}>KBA</div>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>ExamMonitor</span>
            </div>
            <p style={styles.footerText}>
              AI-powered proctoring platform designed for educational institutions. Fair, accurate, and unobtrusive.
            </p>
          </div>

          <div>
            <h4 style={styles.footerHeading}>Platform</h4>
            <div style={styles.footerLinks}>
              <a href="#features" style={styles.footerLink}>Features</a>
              <a href="#how-it-works" style={styles.footerLink}>How It Works</a>
            </div>
          </div>

          <div>
            <h4 style={styles.footerHeading}>Support</h4>
            <div style={styles.footerLinks}>
              <a href="mailto:support@kbaexam.com" style={styles.footerLink}>Contact Us</a>
              <a href="#" style={styles.footerLink}>Documentation</a>
            </div>
          </div>

          <div>
            <h4 style={styles.footerHeading}>Legal</h4>
            <div style={styles.footerLinks}>
              <a href="#" style={styles.footerLink}>Privacy Policy</a>
              <a href="#" style={styles.footerLink}>Terms of Service</a>
            </div>
          </div>
        </div>

        <div style={styles.footerBottom}>
          <span>© {new Date().getFullYear()} KBA ExamMonitor. All rights reserved.</span>
        </div>
      </footer>

    </div>
  );
};

const styles = {
  page: {
    fontFamily: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif",
    overflowX: 'hidden', color: '#1a1a1a'
  },

  // NAV
  nav: {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
    padding: '14px 0', transition: 'all 0.3s ease'
  },
  navInner: {
    maxWidth: 1160, margin: '0 auto', padding: '0 24px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
  },
  logoWrap: { display: 'flex', alignItems: 'center', gap: 10 },
  logoMark: {
    width: 32, height: 32, borderRadius: 8,
    background: 'linear-gradient(135deg,#4cbb4c,#2e9e2e)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontWeight: 800, fontSize: 13
  },
  logoText: { fontWeight: 700, fontSize: 17, color: '#1a1a1a', letterSpacing: -0.3 },
  navLinks: { display: 'flex', alignItems: 'center', gap: 28, className: 'nav-links-desktop' },
  navLink: {
    color: '#666', textDecoration: 'none', fontSize: 14,
    fontWeight: 500, transition: 'color 0.2s'
  },
  hamburger: {
    display: 'none', background: 'none', border: 'none',
    color: '#1a1a1a', fontSize: 24, cursor: 'pointer'
  },
  mobileMenu: {
    padding: '16px 24px', background: '#fff',
    borderTop: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: 14
  },
  mobileLink: { color: '#333', textDecoration: 'none', fontSize: 15, fontWeight: 500 },

  // HERO
  hero: {
    minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: 48,
    background: 'linear-gradient(150deg, #f8faf8 0%, #e8f5e9 30%, #f0faf0 60%, #f5f5f5 100%)',
    padding: '120px 24px 80px', position: 'relative', overflow: 'hidden'
  },
  heroGlow1: {
    position: 'absolute', width: 500, height: 500, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(76,187,76,0.10) 0%, transparent 70%)',
    top: -200, left: -100, pointerEvents: 'none'
  },
  heroGlow2: {
    position: 'absolute', width: 400, height: 400, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(76,187,76,0.08) 0%, transparent 70%)',
    bottom: -100, right: -80, pointerEvents: 'none'
  },
  heroGrid: {
    position: 'absolute', inset: 0,
    backgroundImage: 'radial-gradient(rgba(76,187,76,0.06) 1px, transparent 1px)',
    backgroundSize: '40px 40px', pointerEvents: 'none'
  },
  heroContent: { maxWidth: 520, position: 'relative', zIndex: 1, flex: '0 0 520px' },
  heroTitle: {
    fontSize: 'clamp(32px, 4.5vw, 52px)', fontWeight: 800, lineHeight: 1.15,
    color: '#1a1a1a', margin: '0 0 20px', letterSpacing: -1
  },
  gradientText: {
    background: 'linear-gradient(135deg, #2e7d32 0%, #4cbb4c 60%, #a8e063 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
  },
  heroDesc: {
    fontSize: 'clamp(15px, 1.6vw, 17px)', color: '#555', lineHeight: 1.7,
    margin: '0 0 32px'
  },
  codeInputWrapper: {
    display: 'flex', gap: 8, maxWidth: 460,
    background: '#fff', borderRadius: 12, padding: 4,
    border: '1px solid #e0e0e0', boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
  },
  codeInput: {
    flex: 1, background: 'transparent', border: 'none', outline: 'none',
    color: '#1a1a1a', fontSize: 15, padding: '12px 16px', fontFamily: 'inherit'
  },
  joinBtn: {
    padding: '12px 28px', borderRadius: 9, border: 'none',
    background: 'linear-gradient(135deg,#4cbb4c,#2e9e2e)', color: '#fff',
    fontWeight: 600, fontSize: 15, cursor: 'pointer',
    transition: 'all 0.25s', whiteSpace: 'nowrap'
  },
  heroHint: { fontSize: 13, color: '#999', margin: '16px 0 0' },

  // HERO VISUAL
  heroVisual: { flex: '0 0 400px', position: 'relative', zIndex: 1, animation: 'float 5s ease-in-out infinite' },
  mockWindow: {
    background: '#fff', borderRadius: 14, overflow: 'hidden',
    border: '1px solid #e8e8e8', boxShadow: '0 20px 60px rgba(0,0,0,0.08)'
  },
  mockHeader: {
    padding: '10px 14px', display: 'flex', alignItems: 'center',
    background: '#fafafa', borderBottom: '1px solid #eee'
  },
  mockDot: { width: 10, height: 10, borderRadius: '50%', marginRight: 6 },
  mockBody: {
    height: 200, background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden'
  },
  mockBadge: {
    padding: '6px 14px', borderRadius: 20, background: 'rgba(76,187,76,0.15)',
    color: '#4cbb4c', fontWeight: 700, fontSize: 12, border: '1px solid rgba(76,187,76,0.25)',
    zIndex: 1
  },
  mockScanline: {
    position: 'absolute', inset: 0,
    background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(76,187,76,0.03) 2px, rgba(76,187,76,0.03) 4px)',
    pointerEvents: 'none'
  },
  mockFooter: {
    padding: '8px 14px', display: 'flex', justifyContent: 'space-between',
    background: '#fafafa', borderTop: '1px solid #eee',
    fontSize: 11, color: '#888', fontWeight: 600
  },

  // TRUST BAR
  trustBar: { background: '#fff', borderBottom: '1px solid #f0f0f0', padding: '40px 24px' },
  trustGrid: {
    maxWidth: 900, margin: '0 auto',
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 32
  },
  statValue: { fontSize: 'clamp(30px, 4vw, 42px)', fontWeight: 800, color: '#4cbb4c', lineHeight: 1 },
  statLabel: { fontSize: 14, color: '#888', marginTop: 8 },

  // SECTIONS
  sectionHeader: { textAlign: 'center', maxWidth: 640, margin: '0 auto 48px' },
  sectionTitle: {
    fontSize: 'clamp(26px, 3.5vw, 38px)', fontWeight: 800,
    lineHeight: 1.2, margin: '12px 0 0', letterSpacing: -0.5
  },
  sectionSub: { fontSize: 15, color: '#666', lineHeight: 1.7, margin: '12px 0 0' },

  // FEATURES
  featuresSection: { padding: '100px 24px', background: '#fafafa' },
  featuresGrid: {
    maxWidth: 1100, margin: '0 auto',
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20
  },
  featureCard: {
    background: '#fff', borderRadius: 14, padding: '32px 28px',
    border: '1px solid #e8e8e8', transition: 'all 0.3s ease', cursor: 'default',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
  },
  featureIcon: {
    width: 44, height: 44, borderRadius: 12,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 20, marginBottom: 16, background: '#e8f5e9', color: '#2e7d32'
  },
  featureTitle: { margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: '#1a1a1a' },
  featureDesc: { margin: 0, fontSize: 14, lineHeight: 1.7, color: '#666' },

  // HOW IT WORKS
  howSection: {
    padding: '100px 24px',
    background: 'linear-gradient(150deg, #0f1a0f 0%, #111827 50%, #0a0f0a 100%)'
  },
  stepsContainer: {
    maxWidth: 600, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 0
  },
  stepRow: { display: 'flex', gap: 20, alignItems: 'flex-start' },
  stepNumWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 },
  stepNum: {
    width: 44, height: 44, borderRadius: '50%',
    background: 'linear-gradient(135deg,#4cbb4c,#2e9e2e)',
    color: '#fff', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontWeight: 800, fontSize: 16
  },
  stepLine: {
    width: 2, flex: 1, minHeight: 32,
    background: 'linear-gradient(180deg, rgba(76,187,76,0.4), transparent)',
    marginTop: 4
  },
  stepContent: { paddingBottom: 40 },
  stepTitle: { margin: '0 0 6px', fontSize: 18, fontWeight: 700, color: '#fff' },
  stepDesc: { margin: 0, fontSize: 14, lineHeight: 1.7, color: '#999' },

  // CTA
  ctaSection: {
    padding: '80px 24px', textAlign: 'center',
    background: 'linear-gradient(150deg, #0f1a0f, #111827)',
    borderTop: '1px solid rgba(255,255,255,0.06)'
  },
  ctaBtn: {
    padding: '14px 40px', borderRadius: 10, border: 'none',
    background: 'linear-gradient(135deg,#4cbb4c,#2e9e2e)', color: '#fff',
    fontWeight: 600, fontSize: 16, cursor: 'pointer',
    transition: 'all 0.25s'
  },

  // FOOTER
  footer: {
    background: '#0a0a0a', padding: '56px 24px 28px',
    borderTop: '1px solid rgba(255,255,255,0.04)'
  },
  footerInner: {
    maxWidth: 1100, margin: '0 auto',
    display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 40
  },
  footerBrand: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
  logoMarkSmall: {
    width: 28, height: 28, borderRadius: 6,
    background: 'linear-gradient(135deg,#4cbb4c,#2e9e2e)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontWeight: 800, fontSize: 11
  },
  footerText: { color: '#666', fontSize: 13, lineHeight: 1.7, margin: 0 },
  footerHeading: {
    color: '#fff', fontSize: 12, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14
  },
  footerLinks: { display: 'flex', flexDirection: 'column', gap: 10 },
  footerLink: {
    color: '#666', textDecoration: 'none', fontSize: 14,
    transition: 'color 0.2s'
  },
  footerBottom: {
    maxWidth: 1100, margin: '40px auto 0', paddingTop: 24,
    borderTop: '1px solid rgba(255,255,255,0.04)',
    display: 'flex', justifyContent: 'center', color: '#444', fontSize: 13
  }
};

export default LandingPage;
